"use client";
import Link from "next/link";
import { useEffect, useSyncExternalStore } from "react";

const CONSENT_KEY = "cookie_consent";
const CONSENT_EVENT = "cookie-consent-change";

const consentStore = {
  get: (): boolean | null => {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (raw === null || raw === "undefined") return null;
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === "boolean" ? parsed : null;
    } catch {
      return null;
    }
  },
  getServer: (): undefined => undefined,
  subscribe: (onChange: () => void) => {
    window.addEventListener(CONSENT_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  },
  set: (value: boolean) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
    window.dispatchEvent(new Event(CONSENT_EVENT));
  },
};

export default function CookieBanner() {
  const cookieConsent = useSyncExternalStore(
    consentStore.subscribe,
    consentStore.get,
    consentStore.getServer,
  );

  // Pushing the stored answer into gtag on every load, not just on the click
  // that made it - gtag starts each page denied until told otherwise.
  useEffect(() => {
    if (typeof cookieConsent !== "boolean") return;
    const gtag = (window as any).gtag;
    if (typeof gtag !== "function") return;
    gtag("consent", "update", {
      ad_storage: cookieConsent ? "granted" : "denied",
      analytics_storage: cookieConsent ? "granted" : "denied",
    });
  }, [cookieConsent]);

  return (
    <>
      {cookieConsent === null && (
        <div className="mx-auto max-w-4xl fixed bottom-2 md:bottom-4 left-2 md:left-4 right-2 md:right-4 flex px-3 md:px-4 py-3 justify-between items-center flex-col sm:flex-row gap-2 sm:gap-4 rounded-lg shadow z-[100] bg-[#333333] text-white">
          <p className="text-xs sm:text-sm text-center sm:text-start">
            We use cookies so that your browsing experience on our website is
            the best it can be. By using our site, you confirm that you have
            read and understood our{" "}
            <Link className="text-accent hover:underline" href="/cookie-policy">
              Cookie Policy
            </Link>{" "}
            &{" "}
            <Link
              className="text-accent hover:underline"
              href="/privacy-policy"
            >
              Privacy Policy
            </Link>
            .
          </p>
          <div className="flex gap-2 justify-end items-center h-fit">
            <button
              onClick={() => consentStore.set(false)}
              className="px-5 py-2 text-xs sm:text-sm text-gray-300 rounded-md bg-dark border-gray-900"
            >
              Decline
            </button>
            <button
              onClick={() => consentStore.set(true)}
              className="bg-gray-900 px-4 sm:px-5 text-xs sm:text-sm font-semibold text-center py-2 text-dark bg-accent rounded-lg"
            >
              Allow
            </button>
          </div>
        </div>
      )}
    </>
  );
}
